import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  IconButton,
} from "@mui/material";
import PlayCircleFilledWhiteIcon from "@mui/icons-material/PlayCircleFilledWhite";
import MSwitch from "../../common/switch";
import {
  disableAutomation,
  enableAutomation,
  runningAutomation,
} from "../../automation/automation-service";
import useApiCall from "../../common/api-call";
import { useRecoilState } from "recoil";
import { userInfoState } from "../../global-state";
import { useToast } from "../../common/toast";

const AutomationTitle1Widget = ({ widget, onStateUpdate }) => {
  const { apiCall } = useApiCall();
  const [userInfo] = useRecoilState(userInfoState);
  const { successToast, errorToast } = useToast();

  const [title, setTitle] = useState(widget.title);
  const [subtitle, setSubtitle] = useState(widget.subtitle);
  const [titleError, setTitleError] = useState(false);
  const [tempTitle, setTempTitle] = useState(widget.title);
  const [tempSubtitle, setTempSubtitle] = useState(widget.subtitle);
  const [saving, setSaving] = useState(false);
  const [titleLength, setTitleLength] = useState(0); // 0: normal, 1: bit long, 2: too long
  const titleRef = useRef(null);

  const [automationType, setAutomationType] = useState("");
  const [automationId, setAutomationId] = useState("");
  const [running, setRunning] = useState(false);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    setTitle(widget.title);
    setSubtitle(widget.subtitle);
    setAutomationType(widget.automationType);
    setAutomationId(widget.automationId);
    setRunning(widget.running);
    setEnabled(widget.enabled);
  }, [widget]);

  useEffect(() => {
    if (titleRef.current) {
      setTimeout(() => {
        const element = titleRef.current;
        const ratio = element.scrollWidth / element.clientWidth;
        if (ratio <= 1) {
          setTitleLength(0); // normal
        } else if (ratio <= 1.2) {
          setTitleLength(1); // bit long
        } else {
          setTitleLength(2); // too long
        }
      }, 0);
    }
  }, [title, titleRef]);

  const handleEnableDisable = async (event, enableDisable) => {
    if (enableDisable) {
      const success = await enableAutomation(
        apiCall,
        userInfo.activeScope.id,
        automationId
      );
      if (success) {
        successToast("Enabled.");
        setEnabled(true);
        onStateUpdate &&
          onStateUpdate({
            ...widget,
            enabled: true,
          });
      } else {
        errorToast("Error to enable the automation.");
      }
    } else {
      const success = await disableAutomation(
        apiCall,
        userInfo.activeScope.id,
        automationId
      );
      if (success) {
        successToast("Disabled.");
        setEnabled(false);
        onStateUpdate &&
          onStateUpdate({
            ...widget,
            enabled: false,
          });
      } else {
        errorToast("Error to disable the automation.");
      }
    }
  };

  const handleStartTaskFlow = async () => {
    const success = await runningAutomation(
      apiCall,
      userInfo.activeScope.id,
      automationId
    );
    if (success) {
      successToast("Started");
    } else {
      errorToast("Error!");
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        p: 1,
        boxSizing: "border-box",
        position: "relative",
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          gap: 1,
          position: "relative",
        }}
      >
        <Box
          className="automation-title-trigger"
          sx={{
            flex: "1 1 auto",
            overflow: "hidden",
            minWidth: 0,
            mr: 4,
          }}
        >
          <Typography
            ref={titleRef}
            variant="h2"
            sx={{
              fontSize:
                titleLength === 0
                  ? "18px"
                  : titleLength === 1
                  ? "16px"
                  : "14px",
              fontWeight: 500,
              lineHeight: titleLength === 2 ? 1 : 1,
              mb: 0.5,
              overflow: "hidden",
              textOverflow: titleLength === 2 ? "clip" : "ellipsis",
              whiteSpace: titleLength === 2 ? "normal" : "nowrap",
              WebkitLineClamp: titleLength === 2 ? 2 : 1,
              WebkitBoxOrient: "vertical",
              display: titleLength === 2 ? "-webkit-box" : "flex",
              alignItems: titleLength === 2 ? "flex-start" : "center",
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              fontSize:
                titleLength === 2
                  ? "10px"
                  : titleLength === 1
                  ? "11px"
                  : "12px",
              lineHeight: 1.2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: "text.secondary",
            }}
          >
            {subtitle}
          </Typography>
        </Box>

        <Box
          sx={{
            position: "absolute",
            right: 0,
            top: "50%",
            transform: "translateY(-50%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "32px",
            height: "32px",
            minWidth: "32px",
          }}
        >
          {automationType === "RECURRING" ? (
            <MSwitch
              checked={enabled}
              onChange={handleEnableDisable}
              inputProps={{ "aria-label": "controlled" }}
              size="small"
            />
          ) : running ? (
            <CircularProgress size={20} />
          ) : (
            <IconButton
              size="small"
              onClick={(e) => {
                handleStartTaskFlow();
                e.stopPropagation();
              }}
              sx={{
                p: 0.5,
                "&.Mui-disabled": {
                  opacity: 0.5,
                },
                color: "primary.main",
              }}
            >
              <PlayCircleFilledWhiteIcon fontSize="medium" />
            </IconButton>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AutomationTitle1Widget;
